import { useEffect, useMemo } from "react";
import useSWRInfinite from "swr/infinite";
import { getClientFilesPage } from "../services/file-monitor-service";
import { ClientFilesPage, UseClientFilesModel } from "../types";

const CLIENT_FILES_KEY = "/file-monitor/clients/files";

type ClientFilesPageKey = [typeof CLIENT_FILES_KEY, number, number];

// Loads a client's files a page at a time, newest first. Each page is its own
// SWR cache entry, and the next page is requested as soon as one arrives, so
// files show straight away while the rest load.
// eslint-disable-next-line import/prefer-default-export
export const useClientFiles = (
  clientId: number | null,
): UseClientFilesModel => {
  const getKey = (
    pageIndex: number,
    previousPage: ClientFilesPage | null,
  ): ClientFilesPageKey | null => {
    if (clientId === null) return null;
    if (pageIndex === 0) return [CLIENT_FILES_KEY, clientId, 0];
    if (previousPage?.nextSkip == null) return null;
    return [CLIENT_FILES_KEY, clientId, previousPage.nextSkip];
  };

  const { data, error, size, setSize, mutate } = useSWRInfinite(
    getKey,
    ([, id, skip]: ClientFilesPageKey) => getClientFilesPage(id, skip),
    { revalidateFirstPage: false, revalidateOnFocus: false },
  );

  const pages = useMemo(() => data ?? [], [data]);
  const lastPage = pages.length > 0 ? pages[pages.length - 1] : undefined;
  const isComplete = !!lastPage && lastPage.nextSkip == null;

  // Carry on to the next page once the current one has arrived.
  useEffect(() => {
    if (lastPage?.nextSkip != null && !error && pages.length === size) {
      setSize(size + 1);
    }
  }, [lastPage, error, pages.length, size, setSize]);

  const files = useMemo(() => pages.flatMap((page) => page.files), [pages]);
  const hasPages = pages.length > 0;

  return {
    files,
    totalCount: lastPage ? lastPage.totalCount : null,
    isLoading: clientId !== null && !hasPages && !error,
    isLoadingMore: hasPages && !isComplete && !error,
    error: hasPages ? undefined : error,
    loadMoreError: hasPages ? error : undefined,
    retry: () => {
      mutate();
    },
    mutate: () => {
      mutate();
    },
  };
};
